/**
 * LandingContentContext — loads the CMS-managed content (published version
 * by default, or the draft when ?preview_landing=1 is present) and injects
 * the string overrides into i18next so Landing.jsx / Login.jsx pick them
 * up transparently via useTranslation().
 *
 * Nothing renders differently unless the MASTER_ADMIN has actually saved
 * overrides — otherwise the bundled locales (en/es/fr/ht) are used as-is.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import i18n from "@/i18n";
import api from "@/lib/api";

const LandingContentContext = createContext({ ready: false, toggles: {}, branding: {}, images: {}, partners: [], legalPages: { privacy: {}, terms: {} } });

const SUPPORTED_LANGS = ["en", "es", "fr", "ht"];

/**
 * Apply a flat map of dotted-key overrides into i18next.
 * Example: { "landing.hero_title": "Bienvenue" } → i18n.addResource(lang, "translation", "landing.hero_title", "Bienvenue")
 * The overrides survive language switches because addResource is idempotent.
 */
function applyOverrides(lang, overrides) {
  Object.entries(overrides || {}).forEach(([dottedKey, value]) => {
    if (!value) return;
    i18n.addResource(lang, "translation", dottedKey, value);
  });
}

/**
 * Baseline snapshot of the bundled i18n strings for the keys the CMS may
 * override. Used to restore fallbacks when an admin clears an override.
 * (Populated lazily the first time we see a key.)
 */
const _baseline = { en: {}, es: {}, fr: {}, ht: {} };

function remember(lang, key) {
  if (Object.prototype.hasOwnProperty.call(_baseline[lang], key)) return;
  const bundled = i18n.getResource(lang, "translation", key);
  _baseline[lang][key] = bundled == null ? undefined : bundled;
}

function restoreBaseline(lang, key) {
  const b = _baseline[lang]?.[key];
  if (b === undefined) {
    // Remove custom override: addResource with empty string is treated as
    // present, so we blank via the resource store directly.
    try {
      const store = i18n.store?.data?.[lang]?.translation;
      if (store) {
        // Support dotted keys — walk the object tree if the store is nested.
        // i18n.addResource above created a nested entry: language.translation[key] = value
        // For dotted keys i18n stores them as a single string prop when using
        // keySeparator=false; otherwise it splits. We attempt both.
        if (Object.prototype.hasOwnProperty.call(store, key)) {
          delete store[key];
        }
      }
    } catch (_e) { /* best-effort */ }
  } else {
    i18n.addResource(lang, "translation", key, b);
  }
}

export function LandingContentProvider({ children }) {
  const location = useLocation();
  const previewMode = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get("preview_landing") === "1";
  }, [location.search]);
  const previewLang = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get("preview_lang");
  }, [location.search]);

  // Switch UI language when a preview_lang override is provided by the
  // admin iframe. Runs before overrides are applied so translations use
  // the correct locale.
  useEffect(() => {
    if (previewLang && ["en", "es", "fr", "ht"].includes(previewLang)) {
      i18n.changeLanguage(previewLang);
    }
  }, [previewLang]);

  const [state, setState] = useState({
    ready: false,
    branding: {},
    toggles: {},
    images: {},
    partners: [],
    legalPages: { privacy: {}, terms: {} },
    previewMode,
    updatedAt: null,
    activeOverrides: { en: new Set(), es: new Set(), fr: new Set(), ht: new Set() },
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const url = previewMode
          ? "/landing-settings/admin/draft"
          : "/landing-settings";
        const { data } = await api.get(url);
        if (cancelled) return;
        const overrides = data.translations || {};
        // Clean any overrides that were previously applied then removed.
        setState((prev) => {
          for (const lang of SUPPORTED_LANGS) {
            const previous = prev.activeOverrides[lang] || new Set();
            const nextKeys = new Set(Object.keys(overrides[lang] || {}));
            // Restore any key that used to be overridden but no longer is.
            previous.forEach((k) => {
              if (!nextKeys.has(k)) restoreBaseline(lang, k);
            });
            // Apply new/updated overrides.
            Object.entries(overrides[lang] || {}).forEach(([k, v]) => {
              remember(lang, k);
              i18n.addResource(lang, "translation", k, v);
            });
          }
          return {
            ready: true,
            branding: data.branding || {},
            toggles: data.toggles || {},
            images: data.images || {},
            partners: data.partners || [],
            legalPages: data.legal_pages || { privacy: {}, terms: {} },
            design: data.design || {},
            seo: data.seo || {},
            social: data.social || {},
            appStores: data.app_stores || {},
            previewMode,
            updatedAt: data.updated_at,
            activeOverrides: {
              en: new Set(Object.keys(overrides.en || {})),
              es: new Set(Object.keys(overrides.es || {})),
              fr: new Set(Object.keys(overrides.fr || {})),
              ht: new Set(Object.keys(overrides.ht || {})),
            },
          };
        });
        // Force re-render of consumers by triggering a language event.
        i18n.emit("languageChanged", i18n.language);
      } catch (err) {
        // Content system optional — never break the app if the CMS 404s.
        // eslint-disable-next-line no-console
        console.debug("LandingContent load skipped:", err?.message);
        if (!cancelled) setState((s) => ({ ...s, ready: true }));
      }
    };
    load();
    return () => { cancelled = true; };
  }, [previewMode]);

  return (
    <LandingContentContext.Provider value={state}>
      {children}
    </LandingContentContext.Provider>
  );
}

export function useLandingContent() {
  return useContext(LandingContentContext);
}
